import React from "react";
import { useGetBusinesses } from "@/hooks/useGetBusinesses";
import { AllBusiness, isAllBusiness } from "@/api";
import { useBusinessContext } from "@/hooks/useBusinessContext";
import { OnboardingProgressIndication } from "./OnboardingProgressIndication";
import { LoadingScreen } from "@/LoadingScreen";
import * as Sentry from "@sentry/react";
import { BusinessStatus, useBusinessesStatusGetter } from "@/hooks/useGetBusinessStatus";
import { ErrorDoodle } from "@/components/ErrorDoodle";
import { LogoutButton } from "@/components/LogoutButton";
import { useAuth } from "@/Auth";
import { useT } from "@/i18n";

export function BusinessContextGuard(props: React.PropsWithChildren) {
  const businesses = useGetBusinesses();
  const {
    business: businessContext,
    isLoading: isBusinessContextLoading,
    setBusiness: setBusinessContext,
  } = useBusinessContext();
  const getBusinessStatus = useBusinessesStatusGetter();
  const auth = useAuth();
  const isDemoUser = auth.user?.is_demo;

  const [globalStatus, setGlobalStatus] = React.useState<
    "error" | "onboarding" | "unlicensed" | "undetermined" | "ok"
  >("undetermined");

  const businessStatuses = React.useMemo(() => {
    return businesses.data?.map((b) => getBusinessStatus(b)) ?? [];
  }, [businesses.data, getBusinessStatus]);

  const allBusinessStatus = React.useMemo(() => {
    return getBusinessStatus(AllBusiness);
  }, [getBusinessStatus]);

  React.useEffect(() => {
    if (businesses.isError) {
      setGlobalStatus("error");
      return;
    }
    if (isBusinessContextLoading) {
      setGlobalStatus("undetermined");
      return;
    }
    if (!businesses.isSuccess) {
      setGlobalStatus("undetermined");
      return;
    }

    if (businesses.data.length === 0) {
      Sentry.captureEvent({
        message: "No businesses found",
        level: "error",
      });
      setGlobalStatus("error");
      return;
    }

    if (isDemoUser) {
      setGlobalStatus("ok");
      return;
    }

    const statusDistribution = businessStatuses.reduce(
      (acc, status) => {
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      },
      {} as Record<BusinessStatus, number>,
    );

    if (statusDistribution["undetermined"] > 0) {
      setGlobalStatus("undetermined");
      return;
    }

    if (statusDistribution["unlicensed"] === businessStatuses.length) {
      setGlobalStatus("unlicensed");
      return;
    }

    if (
      (statusDistribution["onboarding"] || 0) +
        (statusDistribution["installation_failed"] || 0) +
        (statusDistribution["status_error"] || 0) ===
      businessStatuses.length
    ) {
      setGlobalStatus("onboarding");
      return;
    }

    if (!statusDistribution["available"]) {
      setGlobalStatus(
        statusDistribution["onboarding"] ||
          statusDistribution["installation_failed"] ||
          statusDistribution["status_error"]
          ? "onboarding"
          : "unlicensed",
      );
      return;
    }

    function selectBusiness(businessId: string) {
      // Rechecking a saved selection must not overwrite a newer selection in another tab.
      if (businessContext!.id !== businessId) setBusinessContext(businessId);
      setGlobalStatus("ok");
    }

    function selectFirstAvailableBusiness() {
      const firstAvailableBusinessIndex = businessStatuses.findIndex(
        (status) => status === "available",
      );
      // we have already checked that there is at least one available business
      selectBusiness(businesses.data![firstAvailableBusinessIndex].id);
    }

    if (isAllBusiness(businessContext.id)) {
      if (allBusinessStatus === "available") {
        selectBusiness(AllBusiness.id);
        return;
      } else {
        selectFirstAvailableBusiness();
        return;
      }
    }

    const businessContextIndex = businesses.data.findIndex((b) => b.id === businessContext.id);
    if (businessContextIndex !== -1) {
      const businessContextStatus = businessStatuses[businessContextIndex];
      if (businessContextStatus === "available") {
        selectBusiness(businessContext.id);
      } else {
        selectFirstAvailableBusiness();
      }
      return;
    }

    Sentry.captureEvent({
      message: "Couldn't find the business context id inside the businesses list",
      level: "warning",
      extra: {
        businesses: businesses.data,
        persistedBusinessContext: businessContext.id,
      },
    });
    selectFirstAvailableBusiness();
    return;
  }, [
    businesses.isSuccess,
    businesses.data,
    businesses.isError,
    isBusinessContextLoading,
    businessStatuses,
    isDemoUser,
    businessContext,
    allBusinessStatus,
    setBusinessContext,
    setGlobalStatus,
  ]);

  if (globalStatus === "undetermined") {
    return <LoadingScreen />;
  }

  if (globalStatus === "error") {
    return <Error />;
  }

  if (globalStatus === "unlicensed") {
    return <LicenseRequired />;
  }

  if (globalStatus === "onboarding") {
    return <OnboardingProgressIndication />;
  }

  return props.children;
}

function LicenseRequired() {
  const businesses = useGetBusinesses();
  const { t, dir, language } = useT();

  return (
    <div
      dir={dir}
      lang={language}
      className="flex min-h-dvh w-full flex-col items-center bg-background px-4 py-4 text-foreground"
    >
      <span className="text-center text-sm text-muted-foreground">
        {t("guard.businesses")}{" "}
        {businesses.data
          ?.map((business) => `${business.display_name} (${business.ref})`)
          .join(", ")}
      </span>

      <div className="flex max-w-2xl flex-1 flex-col items-center justify-center gap-4 py-6 text-center">
        <ErrorDoodle />
        <h1 className="text-3xl font-title tracking-tight text-balance md:text-[32px]">
          {t("guard.licenseTitle")}
        </h1>
        <p className="font-label text-balance text-muted-foreground">{t("guard.licenseBody")}</p>
        <LogoutButton className="mt-2" />
      </div>
    </div>
  );
}

function Error() {
  const { t, dir, language } = useT();
  return (
    <div
      dir={dir}
      lang={language}
      className="flex min-h-dvh w-full flex-col items-center justify-center gap-6 bg-background px-4 py-10 text-center text-foreground"
    >
      <span className="text-lg font-label text-balance">{t("guard.error")}</span>
      <LogoutButton />
    </div>
  );
}
